<template>
    <div class="relative">
        <Button
            @click="toggleNotification"
            icon="pi pi-bell"
            text
            rounded
            data-testid="notification-bell-btn"
            class="!w-10 !h-10 text-gray-500 hover:bg-gray-100"
        />
        <span
            v-if="store.unreadCount > 0 || hasAnnouncements"
            class="absolute top-2 right-2 w-2.5 h-2.5 bg-red-500 border-2 border-white rounded-full pointer-events-none"
        ></span>

        <Popover ref="op" class="w-80 sm:w-96 p-0">
            <div class="flex flex-col max-h-[400px]">
                <div class="p-3 border-b flex justify-between items-center bg-gray-50 rounded-t-lg">
                    <span class="font-semibold text-gray-700">Notifications</span>
                    <Button
                        v-if="store.unreadCount > 0"
                        label="Mark all read"
                        link
                        size="small"
                        data-testid="notification-mark-all-btn"
                        class="!p-0 text-primary text-xs"
                        @click="onMarkAllRead"
                    />
                </div>

                <!-- School-wide announcements (server-driven) -->
                <div v-if="hasAnnouncements" class="border-b bg-amber-50/40" data-testid="notification-announcements">
                    <p class="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Announcements</p>
                    <div
                        v-for="a in announcements"
                        :key="a.id"
                        :data-testid="`notification-announcement-${a.id}`"
                        class="px-3 py-2 border-t first:border-t-0 flex gap-2 items-start"
                    >
                        <i :class="announcementIcon(a.severity)" class="mt-0.5 text-xs p-1 rounded-full"></i>
                        <div class="flex-1 min-w-0">
                            <p class="text-sm font-medium text-gray-900">{{ a.title }}</p>
                            <p class="text-xs text-gray-600 line-clamp-2">{{ a.body }}</p>
                            <a
                                v-if="a.cta_label && a.cta_url"
                                :href="a.cta_url"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="text-xs font-medium text-primary underline"
                            >{{ a.cta_label }}</a>
                        </div>
                        <div class="flex flex-col items-end gap-1 shrink-0">
                            <Button
                                v-if="a.requires_ack"
                                label="Got it"
                                size="small"
                                text
                                class="!p-0 !text-xs"
                                :data-testid="`notification-announcement-ack-${a.id}`"
                                @click="onAnnouncementAction(acknowledge, a.id, 'acknowledge')"
                            />
                            <Button
                                v-if="a.dismissible"
                                icon="pi pi-times"
                                size="small"
                                text
                                rounded
                                class="!w-6 !h-6"
                                aria-label="Dismiss announcement"
                                :data-testid="`notification-announcement-dismiss-${a.id}`"
                                @click="onAnnouncementAction(dismiss, a.id, 'dismiss')"
                            />
                        </div>
                    </div>
                </div>

                <div class="overflow-y-auto flex-1 p-0">
                    <div v-if="store.sortedNotifications.length === 0"
                        class="p-5 text-center text-gray-400 flex flex-col items-center">
                        <i class="pi pi-bell-slash text-lg mb-1.5 opacity-50"></i>
                        <span class="text-sm">No notifications</span>
                    </div>

                    <div
                        v-else
                        v-for="notif in store.sortedNotifications"
                        :key="notif.id"
                        :data-testid="`notification-item-${notif.type}`"
                        class="p-3 border-b hover:bg-gray-50 transition-colors cursor-pointer flex gap-3 relative"
                        :class="{ 'bg-primary-50/50': !notif.read }"
                        @click="handleNotificationClick(notif)"
                    >
                        <!-- Icon based on type -->
                        <div class="mt-1">
                            <i :class="iconClass(notif.type)" class="p-1.5 rounded-full text-xs"></i>
                        </div>

                        <div class="flex-1">
                            <p v-if="notif.title" class="text-sm text-gray-900"
                                :class="{ 'font-semibold': !notif.read }">{{ notif.title }}</p>
                            <p class="text-sm text-gray-700 line-clamp-2">{{ notif.message }}</p>
                            <span class="text-[10px] text-gray-400 mt-1 block">{{ formatDate(notif.created_at) }}</span>
                        </div>

                        <div v-if="!notif.read" class="w-1.5 h-1.5 bg-primary rounded-full mt-2"></div>
                    </div>
                </div>

                <div class="p-2 border-t bg-gray-50 rounded-b-lg text-center">
                    <Button
                        label="View all notifications"
                        link
                        size="small"
                        data-testid="notification-view-all-btn"
                        class="!p-0 text-primary text-xs"
                        @click="goToCentre"
                    />
                </div>
            </div>
        </Popover>
    </div>
</template>

<script setup lang="ts">
import { useNotificationStore, type AppNotification } from '@/store/notificationStore';
import { formatDistanceToNow } from 'date-fns';
import { useToast } from 'primevue/usetoast';
import { useAnnouncements } from '@/composables/useAnnouncements';
import { useNotificationCentre } from '@/composables/useNotificationCentre';
import { notificationIconClass, notificationRouteFor } from '@/utils/notificationDisplay';

const store = useNotificationStore();
const router = useRouter();
const toast = useToast();
const op = ref();

// The bell is mounted once per dashboard shell, so this is where the persisted
// feed is hydrated and the server-side unread badge comes from (#224).
const { markRead, markAllRead } = useNotificationCentre({ first: 20, hydrateStore: true });

// The same widget renders in both shells; the mounted route tells us which
// centre to open (there is no student/admin flag on the user store).
const route = useRoute();
const centreRouteName = computed(
  () => (route.path.startsWith('/student') ? 'student-notifications' : 'admin-notifications'),
);

const goToCentre = () => {
  op.value?.hide();
  router.push({ name: centreRouteName.value });
};

const reportFailure = (verb: string) => {
  toast.add({
    severity: 'error',
    summary: 'Something went wrong',
    detail: `Could not ${verb}. Please try again.`,
    life: 3000,
  });
};

const onMarkAllRead = async() => {
  try {
    await markAllRead();
  } catch (e) {
    reportFailure('mark your notifications read');
  }
};

const {
  announcements, hasAnnouncements, acknowledge, dismiss,
} = useAnnouncements();

const announcementIcon = (severity: string): string => {
  switch (severity?.toLowerCase()) {
  case 'success':
    return 'pi pi-check-circle text-green-600 bg-green-100';
  case 'warning':
    return 'pi pi-exclamation-triangle text-amber-600 bg-amber-100';
  case 'danger':
  case 'critical':
  case 'error':
    return 'pi pi-exclamation-circle text-red-600 bg-red-100';
  default:
    return 'pi pi-megaphone text-primary bg-primary-100';
  }
};

const onAnnouncementAction = async(
  action: (id: string) => Promise<void>, id: string, verb: string,
) => {
  try {
    await action(id);
  } catch (e) {
    toast.add({
      severity: 'error', summary: 'Something went wrong', detail: `Could not ${verb} the announcement. Please try again.`, life: 3000,
    });
  }
};

const toggleNotification = (event: Event) => {
  op.value.toggle(event);
};

const formatDate = (date: string) => {
  try {
    return formatDistanceToNow(new Date(date), { addSuffix: true });
  } catch (e) {
    return date;
  }
};

const iconClass = notificationIconClass;

const handleNotificationClick = (notif: AppNotification) => {
  // Fire-and-forget: navigation below must not wait on the mutation, but a
  // failure still has to surface or the row silently re-appears unread.
  markRead(notif.id).catch(() => reportFailure('mark that notification read'));

  const destination = notificationRouteFor(notif, route.path);
  if (destination) {
    router.push(destination);
    op.value?.hide();
    return;
  }

  if (notif.type === 'ticket_message') {
    store.triggerOpenWidget();
  }
};
</script>
